import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import NavBar from '../components/nav';
import { Footer } from '../components/footer';
import { useCart } from '../contexts/CartContext';
import { FaCreditCard, FaArrowLeft, FaShoppingCart, FaTruck, FaStore, FaMoneyBillWave } from 'react-icons/fa';
import { getStripe, createPaymentIntent, confirmPayment } from '../services/stripeService';
import { processCompletePayment } from '../services/restaurantApi';
import { calculateDeliveryFee, calculateFinalTotal, getDeliveryInfo } from '../utils/deliveryUtils';
import { mergeAddressLines } from '../utils/address';
import { trackConversion } from '../services/promotionTracking';
import {
  getCurrentUser,
  getClientProfile,
  createGuestOrder,
  createCashOnDeliveryOrder,
} from '../services/api';

const PRIMARY = '#054625';
const TERRA = '#EF4C23';
const CREAM = '#FBF9D9';

// Tracker les conversions de promotion pour toute méthode de paiement aboutissant à une commande
const trackPromotionConversions = async (orderId, amount) => {
  try {
    const clickedPromotions = JSON.parse(sessionStorage.getItem('clicked_promotions') || '[]');
    if (clickedPromotions.length > 0 && orderId) {
      for (const promoData of clickedPromotions) {
        if (promoData.promotion_id && promoData.display_format) {
          await trackConversion(promoData.promotion_id, orderId, amount || 0, promoData.display_format);
        }
      }
      sessionStorage.removeItem('clicked_promotions');
    }
  } catch (trackingError) {
    // Erreur silencieuse pour le tracking
  }
};

const Checkout = () => {
  const { t } = useTranslation();
  const { cartItems, clearCart, total } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const orderTypeFromCart = location.state?.orderType || 'delivery';
  const acceptPickupFromCart = location.state?.acceptPickup || false;
  const restaurantSettings = location.state?.restaurantSettings || {};

  const [orderType, setOrderType] = useState(orderTypeFromCart);
  const [paymentMethod, setPaymentMethod] = useState('card');

  const [isLoading, setIsLoading] = useState(false);
  const [stripeConfigured, setStripeConfigured] = useState(true);
  const [paymentError, setPaymentError] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [stripe, setStripe] = useState(null);
  const [elements, setElements] = useState(null);
  const [cardElement, setCardElement] = useState(null);
  const [isElementMounted, setIsElementMounted] = useState(false);
  const [initializationError, setInitializationError] = useState(null);
  const [paymentIntent, setPaymentIntent] = useState(null);
  const [isPaymentReady, setIsPaymentReady] = useState(false);
  const cardElementRef = useRef(null);
  const paymentElementRef = useRef(null);
  const containerRef = useRef(null);

  // État pour les informations de livraison
  const [deliveryData, setDeliveryData] = useState({
    firstName: '',
    lastName: '',
    address: '',
    addressLine2: '',
    addressLine3: '',
    city: '',
    postalCode: '',
    phone: '',
    deliveryInstructions: ''
  });

  // État pour les informations de paiement carte
  const [paymentData, setPaymentData] = useState({
    cardHolder: '',
    email: ''
  });

  useEffect(() => {
    const prefillCheckoutData = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      try {
        const [user, profile] = await Promise.all([
          getCurrentUser().catch(() => null),
          getClientProfile().catch(() => null),
        ]);
        if (!profile && !user) return;
        const firstName = profile?.first_name || '';
        const lastName = profile?.last_name || '';
        const address = profile?.address || '';
        const phone = profile?.phone || '';

        let city = '';
        let postalCode = '';
        if (address) {
          const parts = address.split(',').map(p => p.trim()).filter(Boolean);
          if (parts.length > 1) city = parts[parts.length - 1] || '';
          const zipMatch = address.match(/\b\d{5}\b/);
          postalCode = zipMatch?.[0] || '';
        }

        setDeliveryData(prev => ({
          ...prev,
          firstName: firstName || prev.firstName,
          lastName: lastName || prev.lastName,
          address: address || prev.address,
          city: city || prev.city,
          postalCode: postalCode || prev.postalCode,
          phone: phone || prev.phone,
        }));
      } catch (_) {
        // no-op prefill fallback
      }
    };
    prefillCheckoutData();
  }, []);

  // Nettoyer l'élément Stripe lors du démontage
  useEffect(() => {
    return () => {
      // Démonter l'élément Stripe avant que React ne tente de supprimer le nœud
      if (paymentElementRef.current) {
        try {
          paymentElementRef.current.unmount();
        } catch (error) {
          // Ignorer les erreurs de démontage si l'élément a déjà été démonté
        }
        paymentElementRef.current = null;
      }
    };
  }, []);

  // Initialiser Stripe et préparer automatiquement le paiement au chargement
  useEffect(() => {
    let isMounted = true;
    
    const initializeStripe = async () => {
      try {
        // Nettoyer l'élément existant avant d'en créer un nouveau
        if (paymentElementRef.current) {
          try {
            paymentElementRef.current.unmount();
          } catch (error) {
            // Ignorer les erreurs si l'élément n'existe pas
          }
          paymentElementRef.current = null;
        }
        
        // Utiliser le service Stripe existant
        const { getStripe } = await import('../services/stripeService');
        const stripeInstance = await getStripe();
        
        if (!stripeInstance) {
          throw new Error(t('checkout.errors.stripeInitFailed'));
        }
        
        if (!isMounted) return;
        
        setStripe(stripeInstance);
        setStripeConfigured(true);
        setInitializationError(null);
        
      } catch (error) {
        if (isMounted) {
          setStripeConfigured(false);
          setInitializationError(error.message);
        }
      }
    };
    
    if (cartItems.length > 0) {
    initializeStripe();
    }
    
    return () => {
      isMounted = false;
      // Nettoyer l'élément lors du démontage
      if (paymentElementRef.current) {
        try {
          paymentElementRef.current.unmount();
          paymentElementRef.current = null;
        } catch (error) {
          // Ignorer les erreurs
        }
      }
      // Réinitialiser l'état
      setIsPaymentReady(false);
      setElements(null);
      setPaymentIntent(null);
    };
  }, [cartItems.length, total]);
  
  // Fonction pour préparer automatiquement le paiement
  const autoPreparePayment = async (stripeInstance) => {
    if (!stripeInstance) return;
    
    try {
    setIsLoading(true);
    setPaymentError('');
    
      // Nettoyer l'élément existant s'il existe
      if (paymentElementRef.current) {
        try {
          paymentElementRef.current.unmount();
        } catch (error) {
          // Ignorer les erreurs si l'élément n'existe pas
        }
        paymentElementRef.current = null;
      }
      
      // Vérifier si l'utilisateur est connecté
      const token = localStorage.getItem('access_token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      // 1. Créer le PaymentIntent via l'API backend
      const isPickup = orderType === 'pickup';
      const deliveryFeeForPayment = isPickup ? 0 : calculateDeliveryFee(total || 0, cartItems, restaurantSettings);
      const amountToCharge = (total || 0) + deliveryFeeForPayment;
      const orderData = {
        items: cartItems.map(item => (
          item.item_type === 'grocery_product'
            ? { grocery_shop_product_id: item.id, quantity: item.quantity, supplements: item.supplements || [] }
            : { dish_id: item.id || 1, quantity: item.quantity, supplements: item.supplements || [] }
        )),
        delivery_address: isPickup ? 'ramassage' : `${buildStreetAddress()}, ${deliveryData.city} ${deliveryData.postalCode}`,
        delivery_instructions: deliveryData.deliveryInstructions,
        order_type: orderType,
        delivery_fee: deliveryFeeForPayment,
      };

      // Vérifier que le montant à payer (articles + livraison) est valide
      if (amountToCharge === null || amountToCharge === undefined || isNaN(amountToCharge)) {
        throw new Error(`Total invalide: ${amountToCharge} (type: ${typeof amountToCharge})`);
      }
      if (amountToCharge < 0.5) {
        throw new Error(t('checkout.errors.belowStripeMinimum', { amount: amountToCharge.toFixed(2) }));
      }

      const newPaymentIntent = await createPaymentIntent(
        amountToCharge,
        'eur',
        t('checkout.orderDescription', { count: cartItems.length }),
        orderData
      );

      const clientSecret = newPaymentIntent?.client_secret || newPaymentIntent?.clientSecret || null;
      if (!clientSecret) {
        throw new Error(t('checkout.errors.paymentInitFailed'));
      }
      
      setPaymentIntent(newPaymentIntent);
      
      // 2. Créer les Elements Stripe avec le clientSecret
      const elementsInstance = stripeInstance.elements({
        clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: 'rgb(239, 76, 35)',
          },
        },
      });
      
      setElements(elementsInstance);
      
      // 3. Créer l'élément de paiement
      const paymentElement = elementsInstance.create('payment', {
        layout: 'tabs',
        paymentMethodOrder: ['card', 'apple_pay', 'google_pay'],
        fields: {
          billingDetails: {
            name: 'auto',
            email: 'auto',
            phone: 'auto',
            address: 'auto'
          }
        },
      });
      
      paymentElementRef.current = paymentElement;
      setCardElement(paymentElement);
      
      // 4. Attendre que le DOM soit prêt avant de monter l'élément
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // 5. Monter l'élément dans le DOM en utilisant la ref
      const cardContainer = containerRef.current || document.getElementById('card-element');
      if (cardContainer) {
        containerRef.current = cardContainer;
        
        // Attendre que React ait fini de rendre
        await new Promise(resolve => requestAnimationFrame(resolve));
        
        try {
          paymentElement.mount(containerRef.current);
          setIsPaymentReady(true);
        } catch (mountError) {
          // Si le montage échoue, réessayer après un court délai
          await new Promise(resolve => setTimeout(resolve, 200));
          if (containerRef.current) {
            paymentElement.mount(containerRef.current);
        setIsPaymentReady(true);
          }
        }
      } else {
        throw new Error(t('checkout.errors.paymentContainerNotFound'));
      }
      
    } catch (error) {
      setPaymentError(error.message);
    } finally {
      setIsLoading(false);
    }
  };
  

  // Gestion des changements dans le formulaire de livraison
  const handleDeliveryChange = (e) => {
    const { name, value } = e.target;
    setDeliveryData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Gestion des changements dans le formulaire de paiement
  const handlePaymentChange = (e) => {
    const { name, value } = e.target;
    setPaymentData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Préparer le paiement Stripe manuellement (appelé par le bouton "Préparer le paiement")
  const handlePreparePayment = async () => {
    if (!validateForm()) return;
    if (!stripe) {
      setPaymentError(t('checkout.errors.stripeUnavailable'));
      return;
    }
    await autoPreparePayment(stripe);
  };

  // Gérer la soumission du formulaire (confirmation directe du paiement)
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    if (paymentMethod === 'cash') {
      await handleCashOnDeliveryOrder();
      return;
    }
    
    // Si le paiement n'est pas encore préparé, attendre un peu
    if (!isPaymentReady || !paymentIntent) {
      if (stripe) {
        await autoPreparePayment(stripe);
        // Attendre un peu pour que l'élément soit monté
        await new Promise(resolve => setTimeout(resolve, 500));
      } else {
        setPaymentError(t('checkout.errors.paymentInitializing'));
        return;
      }
    }
    
    // Confirmer le paiement
    await handleConfirmPayment();
  };

  // Le backend n'a qu'un seul champ adresse (rue) : on fusionne les 3 lignes du formulaire.
  const buildStreetAddress = () =>
    mergeAddressLines(deliveryData.address, deliveryData.addressLine2, deliveryData.addressLine3);

  const buildOrderItems = () =>
    cartItems.map((item) => ({
      ...(item.item_type === 'grocery_product'
        ? { grocery_shop_product_id: Number(item.id) }
        : { dish_id: Number(item.id) }),
      quantity: Number(item.quantity) || 1,
      supplements: Array.isArray(item.supplements)
        ? item.supplements
            .map((sup) => ({
              supplement_id: Number(sup.supplement_id ?? sup.id),
              quantity: Number(sup.quantity) || 1,
            }))
            .filter((sup) => Number.isFinite(sup.supplement_id))
        : [],
    }));

  const handleCashOnDeliveryOrder = async () => {
    setIsLoading(true);
    setPaymentError('');
    try {
      const isPickup = orderType === 'pickup';
      const payload = {
        address: isPickup ? 'ramassage' : `${buildStreetAddress()}, ${deliveryData.city} ${deliveryData.postalCode}`.trim(),
        phone: deliveryData.phone,
        email: null,
        paid: false,
        status: 'pending',
        order_type: isPickup ? 'pickup' : 'delivery',
        payment_method: 'cash',
        items: buildOrderItems(),
      };
      const token = localStorage.getItem('access_token');
      const result = token
        ? await createCashOnDeliveryOrder(payload)
        : await createGuestOrder(payload);

      const orderDetails = {
        orderId: result?.id || Math.floor(Math.random() * 1000000),
        amount: totalWithDelivery,
        items: cartItems,
        deliveryAddress: `${buildStreetAddress()}, ${deliveryData.city}`,
        deliveryInstructions: deliveryData.deliveryInstructions,
        estimatedDelivery: '30-45 minutes',
        paymentStatus: isPickup ? 'pickup' : 'cash_on_delivery',
      };
      localStorage.setItem('lastOrder', JSON.stringify(orderDetails));
      await trackPromotionConversions(result?.id, totalWithDelivery);
      clearCart();
      navigate(`/order-confirmation?success=true&order_id=${orderDetails.orderId}`);
    } catch (error) {
      setPaymentError(error?.message || t('checkout.errors.cashOrderFailed'));
    } finally {
      setIsLoading(false);
    }
  };
  
  // Confirmer le paiement
  const handleConfirmPayment = async () => {
    if (!elements || !paymentIntent) {
      setPaymentError(t('checkout.errors.paymentNotReady'));
      return;
    }
    const clientSecret = paymentIntent?.client_secret || paymentIntent?.clientSecret || null;
    if (!clientSecret) {
      setPaymentError(t('checkout.errors.paymentNotReadyMissingSecret'));
      return;
    }
    
    setIsLoading(true);
    setPaymentError('');
    
    try {

      
      // 1. Appeler elements.submit() immédiatement (requis par Stripe)
      const { error: submitError } = await elements.submit();
      
      if (submitError) {
        throw new Error(t('checkout.errors.submissionError', { message: submitError.message }));
      }
      
      // 2. Confirmer le paiement

      const { error, paymentIntent: confirmedIntent } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}/order-confirmation?success=true&order_id=${paymentIntent.order_id || 'temp'}`,
          payment_method_data: {
            billing_details: {
              name: paymentData.cardHolder,
              email: paymentData.email,
            },
          },
        },
        // Sans ça, Stripe.js redirige TOUJOURS vers return_url dès que la confirmation aboutit
        // (même pour une carte sans 3DS), avant que le code ci-dessous (processCompletePayment,
        // qui marque la commande payée) n'ait pu s'exécuter. Avec 'if_required', on ne redirige
        // que pour les moyens de paiement qui l'exigent réellement (Bancontact, PayPal, etc.).
        redirect: 'if_required',
      });

      if (error) {
        throw new Error(error.message);
      }

      if (confirmedIntent.status === 'succeeded') {
        setPaymentSuccess(true);
        
        // Préparer les détails de la commande pour la page de confirmation
        const orderDetails = {
          orderId: paymentIntent.order_id || Math.floor(Math.random() * 1000000),
          paymentIntentId: paymentIntent.id,
          amount: totalWithDelivery,
          items: cartItems,
          deliveryAddress: `${buildStreetAddress()}, ${deliveryData.city}`,
          deliveryInstructions: deliveryData.deliveryInstructions,
          estimatedDelivery: '30-45 minutes',
          paymentStatus: 'succeeded'
        };
        
        // Sauvegarder dans localStorage pour la page de confirmation
        localStorage.setItem('lastOrder', JSON.stringify(orderDetails));
        
        // Appeler le service de paiement complet pour mettre à jour la base de données
        try {
          // Récupérer l'utilisateur connecté si disponible
          const token = localStorage.getItem('access_token');
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          const userId = user?.id || null;
          
          const completePaymentPayload = {
            user_id: userId, // Utilisateur connecté ou null pour guest
            restaurant_id: cartItems[0]?.restaurant_id || 1, // ID du restaurant
            order_data: {
              items: cartItems.map(item => (
                item.item_type === 'grocery_product'
                  ? { grocery_shop_product_id: item.id, quantity: item.quantity, price: item.price, supplements: item.supplements || [] }
                  : { dish_id: item.id, quantity: item.quantity, price: item.price, supplements: item.supplements || [] }
              )),
              total_amount: totalWithDelivery,
              delivery_fee: orderType === 'pickup' ? 0 : deliveryFee,
              subtotal: total,
              order_type: orderType,
            },
            payment_method_data: {
              payment_intent_id: confirmedIntent.id,
              payment_method: 'card',
              amount: totalWithDelivery * 100, // Stripe utilise les centimes
              currency: 'eur'
            },
            delivery_data: {
              address: buildStreetAddress(),
              city: deliveryData.city,
              postal_code: deliveryData.postalCode,
              phone: deliveryData.phone,
              email: paymentData.email || deliveryData.email || 'guest@example.com', // Email du paiement
              instructions: deliveryData.deliveryInstructions,
              first_name: deliveryData.firstName,
              last_name: deliveryData.lastName
            }
          };

          const result = await processCompletePayment(completePaymentPayload);
          orderDetails.orderId = result.order_id || orderDetails.orderId;
          localStorage.setItem('lastOrder', JSON.stringify(orderDetails));

          // Tracker les conversions de promotion si applicable
          await trackPromotionConversions(result.order_id, totalWithDelivery || total || 0);

        } catch (error) {
          // Ne pas bloquer la redirection même si le service échoue, mais garder une trace
          console.error('Erreur lors de la synchronisation post-paiement (processCompletePayment):', error);
        }

        clearCart();
        // redirect: 'if_required' ne redirige pas automatiquement pour une carte confirmée
        // sans action supplémentaire : on navigue nous-mêmes vers la confirmation.
        navigate(`/order-confirmation?success=true&order_id=${orderDetails.orderId}`);
      } else {
        throw new Error(t('checkout.errors.paymentNotConfirmed'));
      }
      
    } catch (error) {
      setPaymentError(error.message);
    } finally {
      setIsLoading(false);
    }
  };



  // Validation du formulaire
  const validateForm = () => {
    const isPickup = orderType === 'pickup';

    if (!deliveryData.firstName || !deliveryData.lastName || !deliveryData.phone) {
      alert(t('checkout.errors.missingContactFields'));
      return false;
    }
    if (!isPickup && (!deliveryData.address || !deliveryData.city || !deliveryData.postalCode)) {
      alert(t('checkout.errors.missingAddressFields'));
      return false;
    }
    if (paymentMethod === 'card' && (!paymentData.cardHolder || !paymentData.email)) {
      alert(t('checkout.errors.missingPaymentFields'));
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (paymentMethod === 'card' && !emailRegex.test(paymentData.email)) {
      alert(t('checkout.errors.invalidEmail'));
      return false;
    }
    return true;
  };

  // Calcul des frais de livraison et du total final
  const deliveryCalculation = calculateFinalTotal(total || 0, cartItems, restaurantSettings);
  const { subtotal, deliveryFee: rawDeliveryFee, total: rawTotalWithDelivery, isFreeDelivery } = deliveryCalculation;
  const deliveryFee = orderType === 'pickup' ? 0 : rawDeliveryFee;
  const totalWithDelivery = orderType === 'pickup' ? (total || 0) : rawTotalWithDelivery;
  const deliveryInfo = orderType === 'delivery' ? getDeliveryInfo(total || 0, cartItems, restaurantSettings) : null;

  const formatEuro = (n) => `${(Number(n) || 0).toFixed(2)} €`;
  const itemLineTotal = (item) => {
    const supplementsTotal = (item.supplements || []).reduce(
      (sum, sup) => sum + (parseFloat(sup.price) || 0) * (parseInt(sup.quantity, 10) || 1),
      0
    );
    return ((parseFloat(item.price) || 0) + supplementsTotal) * (parseInt(item.quantity, 10) || 1);
  };

  const cardStyle = { background: '#FCFBF5', border: '1px solid rgba(5,16,4,0.1)', borderRadius: 'clamp(24px, 3vw, 32px)', padding: 'clamp(22px, 3vw, 32px)', display: 'flex', flexDirection: 'column', gap: '18px' };
  const sectionTitleStyle = { margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: 'clamp(20px, 2.2vw, 26px)', lineHeight: 1.1, display: 'flex', alignItems: 'center', gap: '12px' };
  const stepStyle = { fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.09em', color: TERRA };
  const labelStyle = { fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.62)' };
  const inputStyle = { width: '100%', border: '1px solid rgba(5,16,4,0.14)', borderRadius: '999px', padding: '13px 20px', fontFamily: 'Poppins, sans-serif', fontSize: '15px', background: '#FFFFFF', color: '#051004', outline: 'none' };
  const choiceStyle = (active) => ({
    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '6px', padding: '18px 20px', borderRadius: '24px', cursor: 'pointer', textAlign: 'left',
    border: active ? `2px solid ${PRIMARY}` : '1px solid rgba(5,16,4,0.14)',
    background: active ? CREAM : '#FFFFFF', color: '#051004',
  });
  const noteStyle = { margin: 0, fontFamily: 'Archivo, sans-serif', fontSize: '13.5px', lineHeight: 1.5, padding: '12px 18px', borderRadius: '18px', background: CREAM, color: 'rgba(5,16,4,0.78)' };

  const field = (name, label, { type = 'text', required = false, span = false, value, onChange = handleDeliveryChange } = {}) => (
    <label style={{ display: 'flex', flexDirection: 'column', gap: '7px', gridColumn: span ? '1 / -1' : undefined }}>
      <span style={labelStyle}>{label}</span>
      <input type={type} name={name} value={value ?? deliveryData[name]} onChange={onChange} required={required} style={inputStyle} />
    </label>
  );

  if (cartItems.length === 0) {
    return (
      <>
        <NavBar />
        <main style={{ background: '#FCFBF5', minHeight: '70vh', padding: 'clamp(110px, 12vw, 140px) clamp(16px, 3vw, 40px) clamp(48px, 6vw, 80px)', display: 'flex', justifyContent: 'center' }}>
          <div style={{ ...cardStyle, background: CREAM, border: 'none', alignItems: 'center', textAlign: 'center', maxWidth: '520px', width: '100%' }}>
            <span style={{ width: '72px', height: '72px', borderRadius: '999px', display: 'grid', placeItems: 'center', background: '#FCFBF5', color: PRIMARY }}>
              <FaShoppingCart size={28} />
            </span>
            <h1 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(26px, 3vw, 36px)', letterSpacing: '-0.02em' }}>{t('checkout.emptyCartTitle')}</h1>
            <p style={{ margin: 0, fontSize: '15px', lineHeight: 1.6, color: 'rgba(5,16,4,0.72)' }}>{t('checkout.emptyCartSubtitle')}</p>
            <button
              type="button"
              onClick={() => navigate('/')}
              style={{ background: PRIMARY, color: CREAM, fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '15px', padding: '14px 30px', borderRadius: '999px', border: 'none', cursor: 'pointer' }}
            >
              {t('checkout.discoverDishes')}
            </button>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const submitDisabled = isLoading || (paymentMethod === 'card' && !stripeConfigured);
  let step = 0;
  const nextStep = () => String(++step).padStart(2, '0');

  return (
    <>
      <NavBar />
      <main style={{ background: '#FCFBF5', color: '#051004', fontFamily: 'Poppins, sans-serif' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: 'clamp(100px, 11vw, 130px) clamp(16px, 3vw, 40px) clamp(48px, 6vw, 80px)', display: 'flex', flexDirection: 'column', gap: 'clamp(24px, 3vw, 36px)' }}>
          {/* En-tête */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'flex-start' }}>
            <button
              type="button"
              onClick={() => navigate('/cart')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'Archivo, sans-serif', fontSize: '14px', fontWeight: 600, color: PRIMARY }}
            >
              <FaArrowLeft size={12} />
              {t('checkout.backToCart')}
            </button>
            <h1 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(32px, 4.6vw, 60px)', lineHeight: 1, letterSpacing: '-0.025em' }}>
              {t('checkout.title')}
            </h1>
          </div>

          <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,0.9fr)]" style={{ gap: 'clamp(20px, 3vw, 36px)', alignItems: 'start' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', minWidth: 0 }}>
              {/* Type de commande — visible si le restaurant accepte le retrait */}
              {acceptPickupFromCart && (
                <section style={cardStyle}>
                  <h2 style={sectionTitleStyle}><span style={stepStyle}>{nextStep()}</span>{t('checkout.orderType')}</h2>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
                    <button type="button" onClick={() => setOrderType('delivery')} style={choiceStyle(orderType === 'delivery')}>
                      <FaTruck size={20} color={PRIMARY} />
                      <span style={{ fontWeight: 600, fontSize: '15px' }}>{t('checkout.delivery')}</span>
                      <span style={{ fontSize: '13px', color: 'rgba(5,16,4,0.62)' }}>{t('checkout.atHome')}</span>
                    </button>
                    <button type="button" onClick={() => setOrderType('pickup')} style={choiceStyle(orderType === 'pickup')}>
                      <FaStore size={20} color={PRIMARY} />
                      <span style={{ fontWeight: 600, fontSize: '15px' }}>{t('checkout.pickup')}</span>
                      <span style={{ fontSize: '13px', color: 'rgba(5,16,4,0.62)' }}>{t('checkout.atRestaurant')}</span>
                    </button>
                  </div>
                  {orderType === 'pickup' && <p style={noteStyle}>{t('checkout.pickupReadyNote')}</p>}
                </section>
              )}

              {/* Informations de contact / livraison */}
              <section style={cardStyle}>
                <h2 style={sectionTitleStyle}>
                  <span style={stepStyle}>{nextStep()}</span>
                  {orderType === 'pickup' ? t('checkout.yourDetails') : t('checkout.deliveryAddress')}
                </h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px' }}>
                  {field('firstName', t('checkout.firstName'), { required: true })}
                  {field('lastName', t('checkout.lastName'), { required: true })}
                  {field('phone', t('checkout.phone'), { type: 'tel', required: true, span: true })}
                  {orderType === 'delivery' && (
                    <>
                      {field('address', t('checkout.address'), { required: true, span: true })}
                      {field('addressLine2', t('checkout.addressLine2'), { span: true })}
                      {field('addressLine3', t('checkout.addressLine3'), { span: true })}
                      {field('city', t('checkout.city'), { required: true })}
                      {field('postalCode', t('checkout.postalCode'), { required: true })}
                      <label style={{ display: 'flex', flexDirection: 'column', gap: '7px', gridColumn: '1 / -1' }}>
                        <span style={labelStyle}>{t('checkout.deliveryInstructions')}</span>
                        <textarea
                          name="deliveryInstructions"
                          value={deliveryData.deliveryInstructions}
                          onChange={handleDeliveryChange}
                          rows="3"
                          placeholder={t('checkout.deliveryInstructionsPlaceholder')}
                          style={{ ...inputStyle, borderRadius: '22px', resize: 'vertical' }}
                        />
                      </label>
                    </>
                  )}
                </div>
              </section>

              {/* Mode de paiement */}
              <section style={cardStyle}>
                <h2 style={sectionTitleStyle}><span style={stepStyle}>{nextStep()}</span>{t('checkout.paymentMethod')}</h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
                  <button type="button" onClick={() => setPaymentMethod('card')} style={choiceStyle(paymentMethod === 'card')}>
                    <FaCreditCard size={20} color={PRIMARY} />
                    <span style={{ fontWeight: 600, fontSize: '15px' }}>{t('checkout.online')}</span>
                    <span style={{ fontSize: '13px', color: 'rgba(5,16,4,0.62)' }}>{t('checkout.creditCard')}</span>
                  </button>
                  <button type="button" onClick={() => setPaymentMethod('cash')} style={choiceStyle(paymentMethod === 'cash')}>
                    <FaMoneyBillWave size={20} color={PRIMARY} />
                    <span style={{ fontWeight: 600, fontSize: '15px' }}>{t('checkout.cash')}</span>
                    <span style={{ fontSize: '13px', color: 'rgba(5,16,4,0.62)' }}>
                      {orderType === 'pickup' ? t('checkout.onSite') : t('checkout.onDelivery')}
                    </span>
                  </button>
                </div>

                {paymentMethod === 'card' && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px' }}>
                    {field('cardHolder', t('checkout.cardHolderName'), { required: true, value: paymentData.cardHolder, onChange: handlePaymentChange })}
                    {field('email', t('checkout.email'), { type: 'email', required: true, value: paymentData.email, onChange: handlePaymentChange })}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '7px', gridColumn: '1 / -1' }}>
                      <span style={labelStyle}>{t('checkout.paymentInfo')}</span>

                      {/* "Préparer le paiement" button — hidden once Stripe element is ready */}
                      {!isPaymentReady && (
                        <button
                          type="button"
                          onClick={handlePreparePayment}
                          disabled={isLoading || !stripeConfigured}
                          className="disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '9px', width: '100%', padding: '15px 20px', borderRadius: '999px', border: '2px dashed rgba(5,70,37,0.3)', background: 'transparent', color: PRIMARY, fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14.5px', cursor: 'pointer' }}
                        >
                          {isLoading ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2" style={{ borderColor: PRIMARY }} />
                              {t('checkout.preparingSecurePayment')}
                            </>
                          ) : (
                            <>
                              <FaCreditCard />
                              {t('checkout.prepareSecurePayment')}
                            </>
                          )}
                        </button>
                      )}

                      {/* Stripe element container — always in DOM so the mounted element is preserved */}
                      <div
                        ref={containerRef}
                        id="card-element"
                        className={isPaymentReady ? '' : 'hidden'}
                        style={{ minHeight: '60px', background: '#FFFFFF', border: '1px solid rgba(5,16,4,0.14)', borderRadius: '22px', padding: '16px' }}
                      />
                    </div>
                  </div>
                )}

                {paymentMethod === 'cash' && (
                  <p style={noteStyle}>
                    {orderType === 'pickup' ? t('checkout.cashPickupNote') : t('checkout.cashDeliveryNote')}
                  </p>
                )}
              </section>
            </div>

            {/* Résumé de la commande */}
            <aside className="lg:sticky lg:top-28" style={{ ...cardStyle, background: CREAM, border: 'none', minWidth: 0 }}>
              <h2 style={sectionTitleStyle}>{t('checkout.orderSummary')}</h2>

              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {cartItems.map((item, index) => (
                  <li key={index} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <img
                      src={item.image || '/default-dish.jpg'}
                      alt={item.name}
                      style={{ width: '52px', height: '52px', borderRadius: '16px', objectFit: 'cover', flexShrink: 0 }}
                    />
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <span style={{ fontWeight: 600, fontSize: '14.5px', lineHeight: 1.3 }}>{item.name}</span>
                      <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '13px', color: 'rgba(5,16,4,0.62)' }}>{t('checkout.quantityLabel', { count: item.quantity })}</span>
                      {item.supplements && item.supplements.length > 0 && (
                        <span className="flex flex-wrap gap-1.5" aria-label={t('checkout.supplementsLabel')}>
                          {item.supplements.map((supplement, supIndex) => (
                            <span key={supIndex} style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', padding: '3px 10px', borderRadius: '999px', background: '#FCFBF5', color: PRIMARY }}>
                              {supplement.name} ×{supplement.quantity}
                            </span>
                          ))}
                        </span>
                      )}
                    </div>
                    <span style={{ fontWeight: 600, fontSize: '14.5px', whiteSpace: 'nowrap' }}>{formatEuro(itemLineTotal(item))}</span>
                  </li>
                ))}
              </ul>

              <div style={{ borderTop: '1px solid rgba(5,16,4,0.12)', paddingTop: '16px', display: 'flex', flexDirection: 'column', gap: '10px', fontFamily: 'Archivo, sans-serif', fontSize: '14.5px' }}>
                <div className="flex justify-between">
                  <span style={{ color: 'rgba(5,16,4,0.72)' }}>{t('checkout.subtotal')}</span>
                  <span>{formatEuro(total)}</span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: 'rgba(5,16,4,0.72)' }}>{t('checkout.deliveryFee')}</span>
                  <span style={{ fontWeight: orderType === 'pickup' || isFreeDelivery ? 600 : 400, color: orderType === 'pickup' || isFreeDelivery ? PRIMARY : undefined }}>
                    {orderType === 'pickup' ? t('checkout.freePickup') : isFreeDelivery ? t('checkout.free') : formatEuro(deliveryFee)}
                  </span>
                </div>
                {deliveryInfo && (
                  <div style={{ ...noteStyle, background: '#FCFBF5' }}>
                    <p style={{ margin: 0, fontWeight: 600, color: PRIMARY }}>{deliveryInfo.message}</p>
                    <p style={{ margin: '2px 0 0', fontSize: '12.5px' }}>{deliveryInfo.remainingText}</p>
                  </div>
                )}
                <div className="flex justify-between items-baseline" style={{ borderTop: '1px solid rgba(5,16,4,0.12)', paddingTop: '12px' }}>
                  <span style={{ fontWeight: 600 }}>{t('checkout.total')}</span>
                  <span style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '30px', letterSpacing: '-0.02em' }}>{formatEuro(totalWithDelivery)}</span>
                </div>
              </div>

              {/* Messages d'erreur et de succès */}
              {paymentError && (
                <div className="form-message form-message--error px-4 py-3 text-left" style={{ borderRadius: '18px' }}>
                  <span className="font-semibold">{t('checkout.paymentErrorTitle')}</span>
                  <p className="mt-1">{paymentError}</p>
                  <button onClick={() => setPaymentError('')} className="mt-2 text-sm underline opacity-90 hover:opacity-100" type="button">
                    {t('checkout.close')}
                  </button>
                </div>
              )}

              {paymentSuccess && (
                <div className="form-message form-message--success px-4 py-3 text-left" style={{ borderRadius: '18px' }}>
                  <span className="font-semibold">{t('checkout.paymentSuccessTitle')}</span>
                  <p className="mt-1">{t('checkout.paymentSuccessSubtitle')}</p>
                  <div className="mt-2 flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2 opacity-80" />
                    <span className="text-sm">{t('checkout.autoRedirecting')}</span>
                  </div>
                </div>
              )}

              {/* Bouton unique de paiement */}
              <button
                type="submit"
                disabled={submitDisabled}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', width: '100%', padding: '17px 28px', borderRadius: '999px', border: 'none', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '16px', color: '#FCFBF5', background: submitDisabled ? 'rgba(5,16,4,0.35)' : TERRA, cursor: submitDisabled ? 'not-allowed' : 'pointer' }}
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                    {isPaymentReady ? t('checkout.processingPayment') : t('checkout.preparing')}
                  </>
                ) : paymentMethod === 'card' && !stripeConfigured ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                    {t('checkout.initializingStripe')}
                  </>
                ) : (
                  <>
                    {paymentMethod === 'cash' ? <FaMoneyBillWave size={18} aria-hidden /> : <FaCreditCard size={18} aria-hidden />}
                    {paymentMethod === 'card'
                      ? t('checkout.payAmount', { amount: totalWithDelivery ? totalWithDelivery.toFixed(2) : '0.00' })
                      : orderType === 'pickup'
                      ? t('checkout.confirmOrderPickup')
                      : t('checkout.confirmOrderCashDelivery')}
                  </>
                )}
              </button>
            </aside>
          </form>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default Checkout;
